import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";

const demoAccounts = [
  { label: "Admin", email: "admin@acadence.edu", password: "Admin@123" },
  { label: "Student", email: "student@acadence.edu", password: "Student@123" },
  { label: "Faculty", email: "faculty@acadence.edu", password: "Faculty@123" },
  { label: "HR", email: "hr@acadence.edu", password: "HR@123456" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { document.title = "Sign In — Acadence"; }, []);
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left — Dark editorial panel */}
      <div className="hidden lg:flex lg:w-5/12 bg-[#0F1B3D] noise-bg flex-col justify-between p-12">
        <Link to="/" className="flex items-center gap-2.5">
          <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8">
            <rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66"/>
            <rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/>
            <rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/>
          </svg>
          <span className="font-['DM_Sans'] font-medium text-lg text-[#EEEADF] tracking-[0.08em]">acadence</span>
        </Link>

        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#4B7BF5] mb-6">Ed-ERP Platform</p>
          <blockquote className="font-['Instrument_Serif'] text-[2.6rem] text-[#EEEADF] leading-[1.05]">
            "Education's new<br />
            <em className="italic text-[#4B7BF5]">operating system.</em>"
          </blockquote>
          <p className="font-['DM_Sans'] text-[#EEEADF]/35 text-sm mt-6">
            Trusted by 500+ institutes across India.
          </p>
        </div>

        <div className="border-t border-[#1E2D55] pt-6">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.3em] text-[#EEEADF]/20">© 2026 Acadence</p>
        </div>
      </div>

      {/* Right — Form */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.45 }}
        className="flex-1 bg-[#F5F2EA] flex items-center justify-center p-8"
      >
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8">
              <rect x="1" y="21" width="8" height="8" rx="2" fill="#8B95A8"/>
              <rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/>
              <rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/>
            </svg>
            <span className="font-['DM_Sans'] font-medium text-lg text-[#0F1B3D] tracking-[0.08em]">acadence</span>
          </Link>

          <h1 className="font-['Syne'] font-bold text-3xl text-[#0F1B3D] mb-1.5">Welcome back</h1>
          <p className="font-['DM_Sans'] text-[#7A7468] text-sm mb-10">Sign in to your Acadence account</p>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                data-testid="login-error"
                className="mb-6 px-4 py-3 bg-[#FF3636]/8 border border-[#FF3636]/20 text-[#CC2020] text-sm font-['DM_Sans']"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-7" data-testid="login-form">
            <div>
              <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">Email Address</label>
              <input
                type="email" name="email" value={form.email} onChange={handleChange} required
                placeholder="you@institute.edu"
                data-testid="login-email-input"
                className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 text-[#0F1B3D] font-['DM_Sans'] text-base focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468]">Password</label>
                <a href="mailto:info@acadence.in" className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#7A7468] hover:text-[#0F1B3D] transition-colors">Forgot?</a>
              </div>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"} name="password" value={form.password} onChange={handleChange} required
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 pr-8 text-[#0F1B3D] font-['DM_Sans'] text-base focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  data-testid="toggle-password-btn"
                  className="absolute right-0 bottom-3 text-[#BDBAA8] hover:text-[#0F1B3D] transition-colors"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#0F1B3D] text-[#4B7BF5] font-['Syne'] font-bold text-xs uppercase tracking-widest py-4 hover:bg-[#162044] transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          {/* Demo login */}
          <div className="mt-8 pt-8 border-t border-[#D8D4CB]">
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Quick demo access</p>
            <div className="grid grid-cols-2 gap-2">
              {demoAccounts.map(acc => (
                <button
                  key={acc.label}
                  type="button"
                  data-testid={`demo-${acc.label.toLowerCase()}-btn`}
                  onClick={() => setForm({ email: acc.email, password: acc.password })}
                  className="px-3 py-2.5 border border-[#D8D4CB] text-[#0F1B3D] font-['Syne'] font-bold text-[10px] uppercase tracking-wider hover:bg-[#0F1B3D] hover:text-[#4B7BF5] hover:border-[#0F1B3D] transition-all"
                >
                  {acc.label}
                </button>
              ))}
            </div>
          </div>

          <p className="font-['DM_Sans'] text-center text-sm text-[#7A7468] mt-8">
            No account?{" "}
            <Link to="/register" data-testid="go-register-link" className="font-['Syne'] font-bold text-[#0F1B3D] hover:text-[#4B7BF5] hover:bg-[#0F1B3D] px-1.5 py-0.5 transition-all">
              Sign up
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
